/**
 * Record display helpers: derive description and invoice-number cell from stored data only.
 * 
 * Never emits placeholder tokens, raw record IDs, or fixed sample text.
 * 
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6
 */

import type { FinancialRecord } from '@akfdev-com/shared';

/**
 * Result of deriving a record description.
 */
export interface DerivedDescription {
  /** Derived description text (or empty string if no items) */
  text: string;
  /** True if the join exceeds 200 chars and was truncated */
  truncated: boolean;
  /** True if no line items were found */
  empty: boolean;
}

/**
 * Result of deriving an invoice-number cell.
 */
export interface DerivedInvoiceNumberCell {
  /** The stored invoiceNumber, or empty string if none */
  value: string;
  /** True if the record has no invoiceNumber (caller renders '—') */
  empty: boolean;
}

/**
 * Derive a description from a financial record's line items.
 * 
 * - Joins stored line-item descriptions in stored order (separated by ", ")
 * - If the join exceeds 200 characters, truncates to first 200 + sets truncated: true
 * - If no line items or all items have empty descriptions, sets empty: true
 * - Never emits placeholder tokens or raw IDs
 * 
 * @param record - The financial record
 * @returns Derived description with metadata
 */
export function deriveDescription(record: FinancialRecord): DerivedDescription {
  // Check if record has line items
  if (
    !record.items ||
    !Array.isArray(record.items) ||
    record.items.length === 0
  ) {
    return {
      text: '',
      truncated: false,
      empty: true,
    };
  }

  // Join descriptions from line items in stored order, filtering out empty ones
  const descriptions = record.items
    .map((item) => item.description || '')
    .filter((desc) => desc.trim().length > 0);

  // If no non-empty descriptions, treat as empty
  if (descriptions.length === 0) {
    return {
      text: '',
      truncated: false,
      empty: true,
    };
  }

  const joined = descriptions.join(', ');

  // Check if exceeds 200 characters
  if (joined.length > 200) {
    return {
      text: joined.substring(0, 200),
      truncated: true,
      empty: false,
    };
  }

  return {
    text: joined,
    truncated: false,
    empty: false,
  };
}

/**
 * Derive the invoice-number cell from a financial record.
 * 
 * - Returns the stored invoiceNumber if present
 * - If an invoice has no stored invoiceNumber, sets empty: true (caller renders '—')
 * - Never emits a placeholder token
 * 
 * @param record - The financial record
 * @returns Derived invoice-number cell with metadata
 */
export function deriveInvoiceNumberCell(
  record: FinancialRecord,
): DerivedInvoiceNumberCell {
  // Check if invoiceNumber is present and non-empty
  const invoiceNumber = record.invoiceNumber?.trim() || '';

  return {
    value: invoiceNumber,
    empty: invoiceNumber.length === 0,
  };
}
